import {
  Button,
  Flex,
  JaneLogo,
  TextField,
  Typography,
} from '@iheartjane/reefer';
import type { Nullable } from '@iheartjane/types';
import { clsx } from 'clsx';
import { Controller, useForm } from 'react-hook-form';

import { Breadcrumbs } from '@jane/bloom/components/breadcrumbs/breadcrumbs';
import { FormWithBasename } from '@jane/bloom/components/formWithBasename';
import { GoogleSignInButton } from '@jane/bloom/components/googleSignIn/googleSignIn';
import { useCheckJaneUserExists } from '@jane/bloom/hooks/account/useCheckJaneUserExists';
import { useFieldErrorVisibility } from '@jane/bloom/hooks/forms/useFieldErrorVisibility';
import { useScrollInputsIntoView } from '@jane/bloom/hooks/useScrollInputsIntoView';
import { stricterEmailRegex } from '@jane/bloom/utils/strings/stricterEmailRegex';

import styles from './signIn.module.css';

export interface SignInProps {
  googleSsoClientId?: Nullable<string>;
  skipBagAfterAuth?: boolean;
  userEmail?: string;
}

export function SignIn({
  googleSsoClientId,
  skipBagAfterAuth,
  userEmail,
}: SignInProps) {
  useScrollInputsIntoView();
  const { checkUserExists, fetcher } = useCheckJaneUserExists();

  const submit = ({ email }: { email: string }) => {
    checkUserExists(email);
  };

  const {
    control,
    formState: { errors, isValid },
    handleSubmit,
  } = useForm({
    defaultValues: {
      email: userEmail ?? '',
    },
    mode: 'all',
  });

  const { getShowError, setShowError } = useFieldErrorVisibility([
    { id: 'email' },
  ]);

  return (
    <main className={styles['main']}>
      <Breadcrumbs
        breadcrumbs={[{ label: 'Home', to: '/' }, { label: 'Sign in' }]}
        className={styles['loginBreadcrumbs']}
      />
      <Flex
        className={styles['content']}
        alignItems="center"
        flexDirection="column"
      >
        <Typography variant="header" as="h1" mb={24}>
          {"Let's check if you have an account"}
        </Typography>

        <Flex flexDirection="column" maxWidth={435} width="100%">
          <Flex flexDirection="column" m={32}>
            {googleSsoClientId && (
              <>
                <GoogleSignInButton
                  googleSsoClientId={googleSsoClientId}
                  skipBagAfterAuth={skipBagAfterAuth}
                />
                <Typography mt={16} mb={16} textAlign="center">
                  or
                </Typography>
              </>
            )}
            <FormWithBasename
              name="Account Sign In Form"
              onSubmit={handleSubmit(submit)}
            >
              <Controller
                name="email"
                control={control}
                rules={{
                  pattern: {
                    message: 'Invalid email. Please try again.',
                    value: stricterEmailRegex,
                  },
                  required: 'Invalid email. Please try again.',
                }}
                render={({ field }) => (
                  <TextField
                    disableMobileInputStyling
                    type="email"
                    label="Email"
                    /**
                     * @todo unpack a possible naming clash between Controller and field.
                     * With the same input name, the field does not register the incoming
                     * email from 1password autocomplete on Safari.
                     */
                    name="_email"
                    width="100%"
                    placeholder="Enter your email"
                    defaultValue={userEmail}
                    className={clsx({
                      [styles['errorPassTextField']]:
                        getShowError('email') && errors.email,
                    })}
                    onChange={(email) => {
                      field.onChange(email);
                    }}
                    onBlur={() => {
                      setShowError('email', true);
                    }}
                  />
                )}
              />
              {errors.email && getShowError('email') && (
                <Typography variant="mini" color="error" mt={12}>
                  {errors.email.message}
                </Typography>
              )}
              <Button
                label="Continue"
                full
                mt={24}
                loading={fetcher.state !== 'idle'}
                type="submit"
                disabled={!isValid}
                variant="primary"
              />
            </FormWithBasename>
          </Flex>
          <Typography
            as="span"
            variant="mini"
            mx={32}
            textAlign="center"
            color="grays-mid"
            className={styles['lineHeight']}
          >
            Sign in to check your reservations, status updates, and personal
            details—securely backed by{' '}
            <JaneLogo
              size="xs"
              variant="secondary"
              className={styles['janeLogo']}
            />
          </Typography>
        </Flex>
      </Flex>
    </main>
  );
}
